import {
  Alert,
  Box,
  Card,
  Input,
  Label,
  Select,
  Text,
  Title,
  useToast,
} from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { FormField } from '@nimbus-ds/patterns';
import { useEffect, useState } from 'react';
import { TStore, WHATSAPP_COUNTRIES } from '../../types';
import { useApiClients } from '../../context';
import { ToastManager } from '../../utils/toast.utils';
import { formValidators, useForm } from '../../hooks/useForm.hook';
import SubmitButton from '../SubmitButton/SubmitButton';

const ProfileInfo = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const toast = new ToastManager(addToast, t);

  const { HuappsApi } = useApiClients();

  type TProfile = Partial<TStore>;

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const {
    form: profile,
    setForm: setProfile,
    getFieldProps,
    validate,
    setValidations,
  } = useForm<TProfile>({});

  const validations = {
    nome: [formValidators.required()],
    whatsapp_pais: [
      formValidators.required(),
      formValidators.isCountryPhoneCode(),
    ],
    whatsapp_numero: [
      formValidators.required(),
      formValidators.isPhoneByCountry(() => profile.whatsapp_pais),
    ],
  };
  // Aplica as validações dinamicamente
  useEffect(() => {
    setValidations(validations);
  }, [profile.whatsapp_pais]); // se mudar o país, revalida com base nele

  const getProfile = async () => {
    try {
      const { status, data, error } =
        await HuappsApi.get<TProfile>('/store/profile');

      if (status === 200 && data && !error) {
        setProfile(data);
      } else {
        console.warn(`Consultar dados da loja retornou ${status}:`, error);
        setProfile({});
      }
    } catch (error) {
      console.error(`Erro ao consultar dados da loja:`, error);
      setProfile({});
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaveLoading(true);

      if (!validate()) {
        toast.errorAction('formFieldsInvalid');
        throw Error('Preencha os campos obrigatórios');
      }

      const { status, error } = await HuappsApi.put('/store/profile', {
        ...profile,
      });

      if (status === 201 && !error) {
        toast.successAction('save');
      } else {
        toast.errorAction('save');
        throw Error(`Error POST profile: ${error}`);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <Box display="grid" gap="6">
      <Title as="h3">{t('ProfileInfoComponent.Title')}</Title>
      {loading || profile ? (
        <Card>
          <Card.Body>
            <Box display="grid" gap="4">
              {loading ? (
                <>
                  <Input.Skeleton />
                  <Input.Skeleton />
                  <Input.Skeleton />
                  <Input.Skeleton />
                  <Input.Skeleton />
                </>
              ) : (
                <>
                  <FormField.Input
                    aiGenerated={true}
                    label={`${t('ProfileInfoComponent.InputEmailLabel')}`}
                    id="email"
                    value={profile?.email || ''}
                    crossOrigin=""
                    disabled={true}
                  />
                  <FormField.Input
                    aiGenerated={true}
                    label={`${t('ProfileInfoComponent.InputCNPJLabel')}`}
                    id="cnpj"
                    value={profile?.cnpj || ''}
                    crossOrigin=""
                    disabled={true}
                  />
                  <FormField.Input
                    label={`${t('ProfileInfoComponent.InputNameLabel')}`}
                    aiGenerated={true}
                    {...getFieldProps('nome', false)}
                    required={true}
                    disabled={saveLoading}
                    crossOrigin=""
                  />
                  <Box display={'grid'} gap={'2'}>
                    <Label>
                      {`${t('ProfileInfoComponent.InputWhatsappNumberLabel')}`}
                    </Label>
                    <Box display={'flex'} gap={'3'}>
                      <Box maxWidth={'200px'}>
                        <FormField.Select
                          {...getFieldProps('whatsapp_pais', false)}
                          required={true}
                          disabled={saveLoading}
                          crossOrigin=""
                        >
                          <Select.Option
                            label={t('General.Select')}
                            value={''}
                          />
                          {WHATSAPP_COUNTRIES.map(
                            ({
                              label,
                              value,
                            }: {
                              label: string;
                              value: string;
                            }) => (
                              <Select.Option
                                key={value}
                                label={label}
                                value={value}
                              />
                            ),
                          )}
                        </FormField.Select>
                      </Box>
                      <FormField.Input
                        aiGenerated={true}
                        {...getFieldProps('whatsapp_numero', false)}
                        required={true}
                        disabled={saveLoading}
                        crossOrigin=""
                      />
                    </Box>
                    {!profile.whatsapp_pais ||
                      (!profile.whatsapp_numero && (
                        <Alert
                          appearance="danger"
                          title={`${t('ProfileInfoComponent.AlertWhatsappNumberTitle')}`}
                        >
                          <Text>
                            {`${t('ProfileInfoComponent.AlertWhatsappNumberHelper')}`}
                          </Text>
                        </Alert>
                      ))}
                    <Text fontSize={'caption'} color={'neutral-textDisabled'}>
                      {`${t('ProfileInfoComponent.InputWhatsappNumberHelper')}`}
                    </Text>
                  </Box>
                  <SubmitButton
                    appearance="primary"
                    loading={saveLoading}
                    defaultType="save"
                    onClick={saveProfile}
                  />
                </>
              )}
            </Box>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Alert
            appearance="danger"
            title={`${t('ProfileInfoComponent.ErrorStoreTitle')}`}
          >
            {`${t('ProfileInfoComponent.ErrorStoreMessage')}`}
          </Alert>
        </>
      )}
    </Box>
  );
};

export default ProfileInfo;
